import {
    AutomationModel,
    AutomationsFactory,
    Button,
    DataTable,
    DialogHandler,
    EmptyContent,
    fetchRoute,
    FullBar,
    GenericWrapperContext,
    Header,
    Icon,
    LoadingContainer,
    Search,
    SubNav,
    TDataTableColumnsData,
    TDataTableRowData,
    TGenericListReturn,
    TOrder,
    TPromisePoolPartialList,
    TPromisePoolRef,
    TPromisePoolReturnData
} from "@cakemail-org/ui-components-v2"
import { TDropMenuItem } from "@cakemail-org/ui-components-v2/dist/esm/components/dropMenu/types"
import { TSubNavItem } from "@cakemail-org/ui-components-v2/dist/esm/components/subNav/types"
import { Stack } from "@mui/material"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { showDeleteDialog } from "../../../components/dialogs/deleteDialog"
import { showRenameDialog } from "../../../components/dialogs/rename"
import { listsPromisePool } from "../../../utils/promisesPools"
import { ERoutes } from "../../../utils/url"
import { EABTestsNav, TABTestsListing, TABTestWithStats } from "../types"
import { extractContentIds, filterABTestsByStatus, getABTestsCache, getABTestStatus, setABTestsCache } from "../utils"
import { buildABTestRow } from "../utils/buildABTestRow"
import { getColumnsData } from "../utils/getColumnsData"

export function ABTestsListing({ initTab }: TABTestsListing) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const navigate = useNavigate()

    const [openedSearch, setOpenedSearch] = useState(false)
    const [search, setSearch] = useState("")

    const [loading, setLoading] = useState(false)
    const [order, setOrder] = useState<TOrder>("desc")
    const [orderBy, setOrderBy] = useState<string>("created_on")

    const [allABTests, setAllABTests] = useState<TABTestWithStats[]>()
    const [abTests, setABTests] = useState<TGenericListReturn<TABTestWithStats>>()
    const [partialLists, setPartialLists] = useState<TPromisePoolReturnData<TPromisePoolPartialList>>({})
    const [thumbnails, setThumbnails] = useState<Record<number, { pathA?: string; pathB?: string; pathC?: string; pathD?: string }>>({})

    const partialListsPromiseRef = useRef<TPromisePoolRef>(undefined)

    const [tabFilter, setTabFilter] = useState<EABTestsNav>(initTab || EABTestsNav.Draft)

    useEffect(() => {
        if (initTab) {
            setTabFilter(initTab)
        }
    }, [initTab])

    useEffect(fetchABTests, [order, orderBy, tabFilter])

    useEffect(() => {
        return stopPromisePools
    }, [])

    const filteredABTests = useMemo(() => {
        if (!allABTests || allABTests.length === 0) return []

        let filtered = [...allABTests]

        if (search) {
            const searchLower = search.toLowerCase()
            filtered = filtered.filter((abTest) =>
                abTest.name?.toLowerCase().includes(searchLower)
            )
        }

        return filtered
    }, [allABTests, search])

    useEffect(() => {
        if (!allABTests) return

        setABTests({
            data: filteredABTests,
            pagination: {
                count: filteredABTests.length,
                page: 1,
                per_page: 100,
                has_more: false
            }
        })
    }, [filteredABTests, allABTests])

    function stopPromisePools() {
        try {
            partialListsPromiseRef.current?.stop()
            partialListsPromiseRef.current = undefined
        } catch (e) {
            // pass
        }
    }

    function fetchListsWithPromisePool(abTestsList: TABTestWithStats[]) {
        const listIds = [...new Set(abTestsList.map(abTest => abTest.list_id).filter(Boolean))].map(id => ({ id }))

        if (listIds.length > 0) {
            listsPromisePool({ data: listIds, setter: setPartialLists, poolRef: partialListsPromiseRef })
        }
    }

    function fetchABTests() {
        setLoading(true)
        AutomationsFactory.list({
            page: 1,
            perPage: 100,
            sort: order === "asc" ? orderBy : `-${orderBy}`,
            withCount: true
        }).then(async (data) => {
            // Filter to only show A/B tests (editor_version === "manual")
            const abTestsData = (data.data as TABTestWithStats[]).filter(
                (automation: TABTestWithStats) => automation.editor_version === "manual"
            )
            if (tabFilter === EABTestsNav.Active || tabFilter === EABTestsNav.Completed) {
                await Promise.all(
                    abTestsData.map(async (abTest: TABTestWithStats) => {
                        // only fetch stats for committed tests (skip drafts)
                        if (abTest.committed_revision) {
                            const [statsA, statsB, statsC, statsD] = await Promise.all([
                                abTest.getStats({ path: "pathA" } as any).catch(() => null),
                                abTest.getStats({ path: "pathB" } as any).catch(() => null),
                                abTest.getStats({ path: "pathC" } as any).catch(() => null),
                                abTest.getStats({ path: "pathD" } as any).catch(() => null)
                            ])

                            // Wrap stats in expected structure
                            abTest.statsA = statsA ? { data: statsA } : undefined
                            abTest.statsB = statsB ? { data: statsB } : undefined
                            abTest.statsC = statsC ? { data: statsC } : undefined
                            abTest.statsD = statsD ? { data: statsD } : undefined
                        }
                    })
                )
            }

            const filteredByStatus = filterABTestsByStatus(abTestsData, tabFilter as any)
            setAllABTests(filteredByStatus)
            fetchListsWithPromisePool(filteredByStatus)
            fetchThumbnails(filteredByStatus)

        }).catch(() => {
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("general-error-action")
            })
        }).finally(() => {
            setLoading(false)
        })
    }

    async function fetchThumbnails(abTestsList: TABTestWithStats[]) {
        const cache = getABTestsCache()
        const newThumbnails: Record<number, { pathA?: string; pathB?: string; pathC?: string; pathD?: string }> = {}

        await Promise.all(
            abTestsList.map((abTest) => {
                if (cache[abTest.id]?.thumbnails) {
                    newThumbnails[abTest.id] = cache[abTest.id].thumbnails
                    return Promise.resolve()
                }

                return AutomationsFactory.get({ id: abTest.id }).then((automation: any) => {
                    const contentIds = extractContentIds(automation as TABTestWithStats)
                    const thumbnailPromises: Promise<{ path: string; thumbnail?: string }>[] = []

                    if (contentIds.pathA) {
                        thumbnailPromises.push(
                            AutomationModel.getContent(contentIds.pathA)
                                .then((content: any) => {
                                    return { path: "pathA", thumbnail: content.thumbnail }
                                })
                                .catch(() => {
                                    return { path: "pathA", thumbnail: undefined }
                                })
                        )
                    }
                    if (contentIds.pathB) {
                        thumbnailPromises.push(
                            AutomationModel.getContent(contentIds.pathB)
                                .then((content: any) => {
                                    return { path: "pathB", thumbnail: content.thumbnail }
                                })
                                .catch(() => {
                                    return { path: "pathB", thumbnail: undefined }
                                })
                        )
                    }
                    if (contentIds.pathC) {
                        thumbnailPromises.push(
                            AutomationModel.getContent(contentIds.pathC)
                                .then((content: any) => {
                                    return { path: "pathC", thumbnail: content.thumbnail }
                                })
                                .catch(() => {
                                    return { path: "pathC", thumbnail: undefined }
                                })
                        )
                    }
                    if (contentIds.pathD) {
                        thumbnailPromises.push(
                            AutomationModel.getContent(contentIds.pathD)
                                .then((content: any) => {
                                    return { path: "pathD", thumbnail: content.thumbnail }
                                })
                                .catch(() => {
                                    return { path: "pathD", thumbnail: undefined }
                                })
                        )
                    }
                    return Promise.all(thumbnailPromises).then((results) => {

                        const abTestThumbnails: { pathA?: string; pathB?: string; pathC?: string; pathD?: string } = {}
                        results.forEach(result => {
                            if (result.thumbnail) {
                                abTestThumbnails[result.path as "pathA" | "pathB" | "pathC" | "pathD"] = result.thumbnail
                            }
                        })

                        newThumbnails[abTest.id] = abTestThumbnails

                        cache[abTest.id] = {
                            ...cache[abTest.id],
                            thumbnails: abTestThumbnails,
                            name: abTest.name,
                            totalNumberOfSegments: 0,
                            listName: partialLists[abTest.list_id]?.name
                        }
                    })
                }).catch()
            })
        )
        setABTestsCache(cache)
        setThumbnails(prev => ({ ...prev, ...newThumbnails }))
    }

    function getThumbnailUrl(abTest: TABTestWithStats): string | undefined {
        const abTestThumbnails = thumbnails[abTest.id]

        if (!abTestThumbnails) return undefined

        return abTestThumbnails.pathA || abTestThumbnails.pathB || abTestThumbnails.pathC || abTestThumbnails.pathD
    }

    function handleCreateABTest() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.campaignsABCreate })
    }

    function handleChangeTabFilter(_event: any, item: TSubNavItem) {
        let route = ERoutes.campaignsABTest

        if (item.name === EABTestsNav.Active) {
            route = ERoutes.campaignABTestActive
        } else if (item.name === EABTestsNav.Completed) {
            route = ERoutes.campaignABTestComplete
        }

        navigate(fetchRoute({ route: route }))

        setTabFilter(item.name as EABTestsNav)
    }

    function handleToggleSearch() {
        setOpenedSearch(prev => !prev)
    }

    function handleSearchOpen() {
        setSearch("")
        setOpenedSearch(!openedSearch)
    }

    function handleSearchChange(cSearch: string) {
        setSearch(cSearch)

        // Mimic search delay for smooth UX (similar to users listing)
        if (cSearch) {
            setLoading(true)
            setTimeout(() => {
                setLoading(false)
            }, 300)
        } else {
            setLoading(false)
        }
    }

    function handleEditABTest(abTest: TABTestWithStats) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.automationEdit, params: { automationId: abTest.id } })
    }

    function handleViewStats(abTest: TABTestWithStats) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.campaignABTestStats, params: { abtestId: abTest.id } })
    }

    function handleRenameABTest(abTest: TABTestWithStats) {
        showRenameDialog({
            initName: abTest.name,
            resourceKey: "general-abtest_one"
        })
            .then(async ({ value }) => {
                // need to get automation first like we did in cakemail-app
                return AutomationsFactory.get({ id: abTest.id }).then((data) => {
                    data.name = value
                    return abTest.save(data)
                }).catch((error) => {
                    throw error
                })
            })
            .then(() => {
                fetchABTests()
                DialogHandler.hide()
            })
            .catch((e) => {
                if (e) {
                    DialogHandler.show({
                        isExpress: true,
                        type: "error",
                        title: t("general-error-action")
                    })
                }
            })
    }

    function handleDeleteABTest(abTest: TABTestWithStats) {
        showDeleteDialog({
            name: abTest.name || "",
            resourceKey: "general-abtest_one"
        })
            .then(() => {
                return abTest.delete()
            })
            .then(() => {
                DialogHandler.show({
                    isExpress: true,
                    type: "success",
                    title: t("general-resource-deleted", { resource: t("general-abtest_one") })
                })
                fetchABTests()
            })
            .catch((e) => {
                if (e) {
                    DialogHandler.show({
                        isExpress: true,
                        type: "error",
                        title: t("general-error-action")
                    })
                }
            })
    }

    const colsData: TDataTableColumnsData[] = useMemo(getColumnsData({ tabFilter }), [tabFilter])

    function getActionsDropdownOptions(abTest: TABTestWithStats): TDropMenuItem[] {
        const status = getABTestStatus(abTest)

        if (status === "draft") {
            return [
                {
                    isAction: true,
                    text: t("general-rename"),
                    onClick: () => { handleRenameABTest(abTest) }
                },
                {
                    isAction: true,
                    text: t("general-delete"),
                    postContent: <Icon name="Delete18" />,
                    onClick: () => { handleDeleteABTest(abTest) }
                }
            ]
        }

        if (status === "active") {
            return [
                {
                    isAction: true,
                    text: t("abtest-view-plan"),
                    onClick: () => { handleEditABTest(abTest) }
                }
            ]
        }

        // completed actions
        return [
            {
                isAction: true,
                text: t("abtest-view-plan"),
                onClick: () => { handleEditABTest(abTest) }
            },
            {
                isAction: true,
                text: t("general-rename"),
                onClick: () => { handleRenameABTest(abTest) }
            }
        ]
    }

    const rows: TDataTableRowData[] = useMemo(() => abTests?.data.map((abTest) => {
        return buildABTestRow({
            abTest,
            tabFilter,
            partialLists,
            thumbnails,
            getActionsDropdownOptions,
            getThumbnailUrl,
            handleViewStats,
            handleEditABTest
        })
    }) || [], [abTests?.data, tabFilter, partialLists, thumbnails, search])

    let emptyContentTitle = t("abtests-listing-empty-title-drafts")
    let emptyContentDesc = t("abtests-listing-empty-desc-drafts")

    if (tabFilter === "active") {
        emptyContentTitle = t("abtests-listing-empty-title-active")
        emptyContentDesc = t("abtests-listing-empty-desc-active")
    } else if (tabFilter === "completed") {
        emptyContentTitle = t("abtests-listing-empty-title-completed")
        emptyContentDesc = t("abtests-listing-empty-desc-completed")
    }

    return (
        <Stack gap={8}>
            <FullBar
                className={loading ? "loading" : ""}
                left={<Header
                    heading={t("general-abtest_other")}
                    headingVariant="titleL"
                    support={genericWrapper.account?.name}
                />}
                right={[
                    <Button
                        key="create-abtest"
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleCreateABTest}
                        iconOnly
                        disabled={loading}
                    >
                        <Icon name="Add18" />
                    </Button>,
                    <Button
                        key="search-abtest"
                        variant="outlined"
                        size="medium"
                        color="body1"
                        onClick={handleToggleSearch}
                        iconOnly
                        disabled={loading}
                    >
                        <Icon name="Search18" />
                    </Button>
                ]}
            />

            {openedSearch &&
                <Search
                    placeholder={t("abtests-listing-search-placeholder", { status: t(`general-${tabFilter}`) })}
                    initValue={search}
                    className={`abTestsSearch ${loading ? "loading" : ""}`}
                    onChange={handleSearchChange}
                    onClose={handleSearchOpen}
                    useClickAway
                />
            }

            <SubNav
                onClick={handleChangeTabFilter}
                initialSelected={tabFilter}
                items={[
                    { text: t("general-draft"), name: EABTestsNav.Draft },
                    { text: t("general-status-active"), name: EABTestsNav.Active },
                    { text: t("general-completed"), name: EABTestsNav.Completed }
                ]}
            />

            <LoadingContainer loading={!abTests}>
                {abTests && abTests.data.length === 0 && !search &&
                    <EmptyContent
                        title={emptyContentTitle}
                        desc={emptyContentDesc}
                        button={<Button
                            variant="contained"
                            onClick={handleCreateABTest}
                            size="medium"
                            color="secondary"
                            iconOnly>
                            {t("general-create-absplit")}
                        </Button>}
                        iconName="EmptyABTest44"
                    />
                }

                {abTests && abTests.data.length === 0 && search &&
                    <EmptyContent
                        title={t("abtests-listing-empty-search-title")}
                        desc={t("search-empty-desc")}
                        button={<Button
                            variant="contained"
                            onClick={handleCreateABTest}
                            size="medium"
                            color="secondary"
                            iconOnly>
                            {t("general-create-absplit")}
                        </Button>}
                    />
                }

                {abTests && abTests.data.length > 0 &&
                    <DataTable
                        initState={{
                            order,
                            orderBy
                        }}
                        totalCount={abTests.pagination.count}
                        className={`abTestsDataTable ${loading ? "loading" : ""}`}
                        rows={rows}
                        columnsData={colsData}
                        stickyLastCol
                        showPagination={false}
                    />
                }
            </LoadingContainer>
        </Stack>
    )
}